import { gobrainLiveConfig, publicDataDomain, qplayerRestrictedAccounts } from "../../util/config";
import { EDITOR_PLAYER_ID } from "../../util/util";
import { isReelsFeedEnabled } from "../StoryFeed/story-feed-helper";

export namespace PlayerHelper {
    export const ERROR_LABELS = {
        cannotBeViewedYet: "RESTRICTED_CONTAINER_PUBLISH_DATE_HAVENT_REACH", // 'The publish date for this media has not been reached yet',
        encoding: "RESTRICTED_CONTAINER_VIDEO_ENCODING", // 'This video is still being processed',
        forbidden: "RESTRICTED_CONTAINER_ACCESS_FORBIDDEN", // 'Access to this media is currently restricted',
        ipRestriction: "RESTRICTED_CONTAINER_IP_RESTRICTED", // 'You are not within the valid IP-range to view this media',
        notAnymore: "RESTRICTED_CONTAINER_UNPUBLISH_MEDIA", // 'This media has been unpublished',
        notRecognised: "RESTRICTED_CONTAINER_NOT_RECOGNISABLE", // 'Authentication failed, please try again
        videoNotFound: "RESTRICTED_CONTAINER_VIDEO_NOT_FOUND", // Video not found
        mediaNotFound: "RESTRICTED_CONTAINER_MEDIA_NOT_FOUND", // Video not found
        videoCannotPlay: "RESTRICTED_CONTAINER_404_VIDEO_CANNOT_PLAY", // An error has occurred during playback. Try to refresh the page.
        liveNotFound: "RESTRICTED_CONTAINER_404_LIVE_NOT_FOUND", // Live event not found
        liveCannotPlay: "RESTRICTED_CONTAINER_404_LIVE_CANNOT_PLAY", // Live stream offline or playback error. Try to refresh the page.
        videoCorrupted: "RESTRICTED_CONTAINER_VIDEO_CORRUPTED",  // Video is corrupt
        videoTimeout: "RESTRICTED_CONTAINER_524_VIDEO_TIMEOUT", // Video playback timeout. Try to refresh the page.
        liveTimeout: "RESTRICTED_CONTAINER_524_LIVE_TIMEOUT", // Live stream playback timeout. Try to refresh the page.
        cndCache: "RESTRICTED_CONTAINER_416_CDN_CACHE", // Unable to play video due to corrupt CDN cache.
        geoRestricted: "RESTRICTED_CONTAINER_GEO_RESTRICTED", // This video is not available in your country
        domainRestricted: "RESTRICTED_CONTAINER_DOMAIN_RESTRICTED", // This video is not allowed to be viewed from this website
    }

    export const ERROR_TYPES = {
        MediaEncoding: "GoBrain.Dtos.Restrictions.MediaEncoding",
        AccessForbidden: "GoBrain.Dtos.Restrictions.AccessForbidden",
        MediaUnavailableBefore: "GoBrain.Dtos.Restrictions.MediaUnavailableBefore",
        MediaUnavailableAfter: "GoBrain.Dtos.Restrictions.MediaUnavailableAfter",
        DataLoad: "GoBrain.Errors.Data.Load",
        MediaPlayerLoad: "GoBrain.Errors.MediaPlayer.Load",
        IPRangeUnsatisfied: "GoBrain.Dtos.Restrictions.IPRangeUnsatisfied",
        AuthorizationRequired: "GoBrain.Dtos.Restrictions.AuthorizationRequired",
        VideoCorrupted: "GoBrain.Dtos.Data.Corrupted",
        VideoJsLoad: "GoBrain.ModuleAssets.MediaPlayer.MediaPlayers.VideoJs.Exceptions.Load",
        NotificationError: "GoBrain.Errors.Notifications.Notification.Error",
        HeartbeatNetworkError: "GoBrain.Errors.Heartbeat.Network.Error",
        HeartbeatNetworkTimeout: "GoBrain.Errors.Heartbeat.Network.Timeout",
        HeartbeatAccessDenied: "GoBrain.Errors.Heartbeat.AccessDenied"
    }

    // Non-fatal error - add more here if needed
    export const NON_FATAL_ERROR_TYPES: string[] = [
        ERROR_TYPES.NotificationError,
        ERROR_TYPES.HeartbeatNetworkError,
        ERROR_TYPES.HeartbeatNetworkTimeout,
        ERROR_TYPES.HeartbeatAccessDenied
    ];

    export interface PlayerProps {
        mediaData?: any;
        container?: HTMLElement;
        playerId: string;
        playerMedia: string;
        mediaType?: string;
        token?: string;
        accountId?: string;
        playlistId?: string;
        classNames?: string;
        isMediaLive?: boolean;
        isVisible?: boolean;
        isMaintainProportions?: boolean;
        endOfScene?: any;
        settings?: any;
        configParams?: any;
        isStoryPlaylist?: boolean | "";
        /** Story feed only: the clip in focus has played to its end, so the feed can move on. */
        onPlayComplete?: () => void;
        /** Story feed only: this clip is showing picture, so its still frame can be taken down. */
        onPicture?: () => void;
        handlePlayerHover?: () => void;
        layout: "desktop" | "mobile";
    }

    export const createPlayerInstance = ({ playerProps, scenario, onPlayerReady, mediaType }: { playerProps: Omit<PlayerProps, "i18n">; mediaType: string; scenario?: any; onPlayerReady?: (_isReady: boolean) => void }) => {
        const {
            playerId,
            isVisible,
            endOfScene,
            token,
            playerMedia,
            settings,
            accountId,
            playlistId,
            configParams = {},
            isMediaLive,
            isStoryPlaylist,
            container
        } = playerProps;

        if (!accountId) {
            return;
        }

        const {
            autoplay: paramAutoplay,
            mute: paramMute,
            repeat: paramRepeat,
            ignoreAnalytics: paramIgnoreAnalytics,
            language: paramLanguage,
            analyticsCookies: paramAnalyticsCookies,
            subtitlesAuto: paramSubtitlesAuto,
            inline: inlineParam,
            container: containerParam
        } = configParams ?? {};

        const defaultVolume = settings.playbackOptions?.setDefaultVolume ? settings.playbackOptions?.defaultVolumeValue / 100 || 0.5 : 0.5;
        let volume =
            !isVisible || playerId === EDITOR_PLAYER_ID
                ? 0
                : !paramMute
                    ? settings.playbackOptions?.muteAtStart
                        ? 0
                        : defaultVolume
                    : paramMute === 'true'
                        ? 0
                        : defaultVolume;
        // Minimized widget should always be muted at start
        volume = configParams?.fromQplayerWidget === 'true' ? 0 : volume;
        let autoplay =
            playerId === EDITOR_PLAYER_ID
                ? false
                : !isVisible
                    ? false
                    : !paramAutoplay
                        ? settings.playbackOptions?.autostart
                            ? true
                            : false
                        : paramAutoplay === 'true';
        // The feed mounts the neighbouring slides too, so only the one in focus may start.
        // configParams, not the context's playlistId: the same value arrives one effect later, so
        // this is correct on the first render too.
        const isReelsFeed = isReelsFeedEnabled(configParams);
        autoplay = inlineParam === "off" && !!containerParam ? (isReelsFeed ? !!isVisible : true) : autoplay;
        // In a feed, reaching the end is what moves the viewer on - so looping and advancing are the
        // same switch seen from two sides, and the preset's own "replay" is already that switch. It
        // used to be forced off here, which made the feed's behaviour the one thing about playback
        // the preset could not express. The feed declines to advance on the same flag; see
        // handleStoryVideoEnded, which is what makes this hold even if the framework still reports
        // a completion while repeating.
        const repeat = isReelsFeed
            ? !!settings?.playbackOptions?.replay
            : mediaType === 'scenario'
                ? endOfScene?.type === 'loopCurrentScene'
                : !paramRepeat
                    ? settings.playbackOptions?.replay
                        ? true
                        : false
                    : paramRepeat === 'true'
                        ? true
                        : false;

        const moduleSettings = {
            TopControls: { download: { enabled: true }, sharing: { enabled: true } },
            MobileControls: { download: { enabled: true }, sharing: { enabled: true } },
            Data: token
                ? {
                    headers: {
                        'X-Authorization': 'AVP1 ' + token,
                    },
                }
                : {},
            Poster: {
                settings: {
                    // cover being handled on qplayer
                    supportsCovers: false,
                    showPoster: false,
                },
            },
            ChromeCast: {
                name: 'ChromeCast',
                type: 'ChromeCast',
                settings: {
                    supportsChromeCast: settings.bottomControls?.showCastDevices,
                },
            },
            Advertisement: {
                name: 'Advertisement',
                type: 'Advertisement',
                settings: {
                    enabled: settings?.advertisementSettings?.enabled,
                    advertisementServer: settings?.advertisementSettings?.advertisementServer,
                    cTAClickArea: settings?.advertisementSettings?.cTAClickArea,
                },
            },
        };

        let data = publicDataDomain + accountId + '/medias/' + playerMedia;
        if (playlistId && !isStoryPlaylist) {
            data = publicDataDomain + accountId + '/playlists/' + playlistId + '/medias?followRedirects=true';
        }

        const ignoreAnalytics =
            playerId === EDITOR_PLAYER_ID ||
            (paramIgnoreAnalytics ? paramIgnoreAnalytics === 'true' : settings?.additionalSettings?.disableAnalytics === true);
        const language = paramLanguage ? paramLanguage : settings?.accessibilitySettings?.language;

        const defaultSceneBGMediaId = scenario?.defaultSceneBGMediaId ?? "";
        const isDefaultSceneBGMedia = playerMedia === defaultSceneBGMediaId && mediaType === 'scenario';

        const analyticsCookies = paramAnalyticsCookies ? paramAnalyticsCookies === 'true' : !(settings?.additionalSettings?.noCookies === true);

        const embedSettings = {
            config: gobrainLiveConfig,
            data: data,
            widgetId: playerId,
            volume: volume,
            autoplay: configParams?.isModalInstance ? false : isMediaLive && !isDefaultSceneBGMedia ? true : autoplay,
            repeat: repeat,
            language: language === 'dk' ? 'da' : language,
            ignoreAnalytics: ignoreAnalytics,
            moduleSettings: moduleSettings,
            subtitlesAutoOn: !paramSubtitlesAuto ? settings?.playbackOptions?.subtitlesAutoOn : paramSubtitlesAuto === 'true',
            noSchemaData: settings?.additionalSettings?.noSchemaData,
            useCookie: analyticsCookies
        };

        const containerW = container ?? document;
        (window as any).GoBrain.create(containerW.querySelector(`#${playerId}-container`), embedSettings).on('ready', () => {
            onPlayerReady?.(true);
            // The one place a video is mounted before it is wanted, so the one place preloading
            // pays for itself: the clip below the fold is buffered before it is scrolled to.
            if (isVisible || playerId === EDITOR_PLAYER_ID || (mediaType === 'video' && !isReelsFeed)) {
                return;
            }

            if (!(qplayerRestrictedAccounts ?? "").includes(accountId)) {
                setTimeout(() => {
                    const widget = (window as any).GoBrain.widgets(playerId);
                    widget && widget.preload && widget.preload();
                });
            }
        });
    }

    interface PlayerEvents {
        queueItem: (_item: any) => void;
        playable: () => void;
        play: () => void;
        pause: () => void;
        playComplete: () => void;
        position: (_position: number) => void;
        duration: (_duration: number) => void;
        notification: (_event: any) => void;
        volume: (_volume: any) => void;
        error: (_error: any) => void;
        adsStart?: (_data: any) => void;
        adsPlay: (_data: any) => void;
        adsSkipped?: (_data: any) => void;
        adsComplete?: (_data: any) => void;
        adsAllComplete?: (_data: any) => void;
    }
    export const subscribePlayerEvents = (playerId: string, playerEvents: PlayerEvents) => {
        if ((window as any).GoBrain.widgets()[playerId]) {
            Object.keys(playerEvents).forEach((event) => {
                (window as any).GoBrain.widgets()[playerId].on(event, (playerEvents as any)[event]);
            });

            const gobrainVolumeController = document.querySelector('.gobrain-controller-volume') as any;
            if (gobrainVolumeController) {
                gobrainVolumeController.style = '';
            }
        }
    };

    export const unsubscribePlayerEvents = (playerId: string, events: PlayerEvents) => {
        if ((window as any).GoBrain.widgets()[playerId]) {
            Object.keys(events).forEach((event) => {
                (window as any).GoBrain.widgets()[playerId].off(event, (events as any)[event]);
            });
        }
    };

    export const movePlayerToPosition = (playerId: string, positionToMove: number) => {
        (window as any).GoBrain.widgets(playerId)?.position?.(positionToMove);
    };

    export const handleButtonForward = (playerId: string, currentPosition: number | "") => {
        let forwardPosition = (currentPosition === '' ? 0 : currentPosition) + 15;
        (window as any).GoBrain.widgets(playerId)?.position?.(forwardPosition);
    };

    export const handleButtonReward = (playerId: string, currentPosition: number | "") => {
        let rewardPosition = currentPosition === '' || currentPosition < 15 ? 0 : currentPosition - 15;
        (window as any).GoBrain.widgets(playerId)?.position?.(rewardPosition);
    };

    export const onReceiveMessage = ({ event, playerId, isVisible, currentPosition }: { event: any, playerId: string, isVisible: boolean, currentPosition: number | '' }) => {

        if (!isVisible) {
            return;
        }

        if (event.data?.type === 'move-position-from-editor' && event.data?.position !== undefined && event.data?.position !== null) {
            movePlayerToPosition(playerId, event.data?.position);
        }

        if (event.data?.type === 'pause') {
            (window as any).GoBrain.widgets(playerId)?.pause?.();
        }

        if (event.data?.type === 'play') {
            (window as any).GoBrain.widgets(playerId)?.play?.();
        }

        if (event.data?.type === 'forward') {
            handleButtonForward(playerId, currentPosition);
        }

        if (event.data?.type === 'reward') {
            handleButtonReward(playerId, currentPosition);
        }

        if (event.data?.type === 'mute') {
            (window as any).GoBrain.widgets(playerId)?.volume?.(0);
        }

        if (event.data?.type === 'unMute') {
            (window as any).GoBrain.widgets(playerId)?.volume?.(0.5);
        }
    }

    export const setThumbnailForPlayer = (thumbnailUrl: string, setLoading: (_value: boolean) => void) => {
        if (!thumbnailUrl) {
            setLoading(false);
            return;
        }

        [3, 5, 7, 10].map((i) => {
            setTimeout(() => {
                const gobrainPoster = document.querySelector('.gobrain-poster') as any;
                if (gobrainPoster) {
                    gobrainPoster.style.display = 'none';
                    gobrainPoster.style.backgroundImage = `url(${thumbnailUrl})`;
                }

                if (i === 10) {
                    setLoading(false);
                    if (gobrainPoster) {
                        gobrainPoster.style.display = '';
                    }
                }
            }, i * 100);
        });
    };
}